import { EditorContext, useCurrentEditor, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';

import { Extensions } from '@tiptap/core';

import cn from 'classnames';
import { twMerge } from 'tailwind-merge';

import NoCopyPaste from './plugins/NoCopyPaste';
import PlaceholderExtended from './plugins/PlaceholderExtended';
import UniqueId from './plugins/UniqueId';
import Content from './plugins/Content';
import { ExecutableCodeBlockNode } from './plugins/coding/ExecutableCodeNode';

import MarkdownExtension from './plugins/markdown';
import { memo, MutableRefObject, useEffect, useRef } from 'react';
import { EditorRendererContext } from './Editor';
import Comment from './plugins/comment';

import { BoldToken, ItalicToken, BulletListToken, MathToken } from '@scale/shared/types/config';

import ReviewerCopilot from './plugins/ReviewerCopilot';

import Alert from './plugins/Alert';

import TextDirection from './plugins/TextDirection';

export interface DefaultTokenConfig {
  boldToken: BoldToken;
  italicToken: ItalicToken;
  bulletListToken: BulletListToken;
  mathToken: MathToken;
}

export interface MarkdownEditorContextProps {
  className?: string;
  children: React.ReactNode;
  extensions?: Extensions;
  initialContent?: string;
  defaultTokens?: DefaultTokenConfig;
  // TODO: @Lajos This is a bad way to handle these options. For the time being let's leave it like this
  enableMath?: boolean;
  enableCourseTools?: boolean;
  enableHtml?: boolean;
}

interface DefaultTokenConfigProps {
  defaultTokens?: DefaultTokenConfig;
}

export const DEFAULT_TOKENS = {
  boldToken: BoldToken.ASTERISK,
  italicToken: ItalicToken.ASTERISK,
  bulletListToken: BulletListToken.ASTERISK,
  mathToken: MathToken.DOLLARS,
};

function DefaultTokenConfigWatcher({ defaultTokens = DEFAULT_TOKENS }: DefaultTokenConfigProps) {
  const { editor } = useCurrentEditor();
  useEffect(() => {
    if (!editor) return;
    editor.storage.markdownExtensions.boldToken = defaultTokens.boldToken;
    editor.storage.markdownExtensions.italicToken = defaultTokens.italicToken;
    editor.storage.markdownExtensions.bulletListToken = defaultTokens.bulletListToken;
    editor.storage.markdownExtensions.mathToken = defaultTokens.mathToken;
  }, [editor, defaultTokens]);
  return null;
}

function MarkdownEditorContext({
  className,
  children,
  extensions,
  initialContent,
  defaultTokens = DEFAULT_TOKENS,
  enableMath = false,
  enableCourseTools = false,
  enableHtml = false,
}: MarkdownEditorContextProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const editor = useEditor(
    {
      content: initialContent,
      immediatelyRender: false,
      extensions: [
        UniqueId,
        MarkdownExtension.configure({
          enableMath,
          enableCourseTools,
          enableHtml,
          boldToken: defaultTokens.boldToken,
          italicToken: defaultTokens.italicToken,
          bulletListToken: defaultTokens.bulletListToken,
          mathToken: defaultTokens.mathToken,
        }),
        Content.configure({
          type: 'markdown',
          isCourseEditor: enableCourseTools,
        }),
        StarterKit.configure({
          text: false,
          bold: false,
          italic: false,
          bulletList: false,
          listItem: false,
          codeBlock: false,
          paragraph: false,
        }),
        PlaceholderExtended,
        NoCopyPaste,
        ExecutableCodeBlockNode,
        Comment.configure({
          classes: {
            base: 'comment hover:cursor-pointer',
            selection: 'bg-warning-100',
            comment: 'bg-primary-100 hover:bg-primary-200',
            selectedComment: 'bg-primary-200',
          },
        }),
        ReviewerCopilot,
        Alert,
        TextDirection,
        ...(extensions ?? []),
      ],
      // set initial editor options, do not add dynamic options here!
      editorProps: {
        attributes: {
          class: twMerge(
            cn(
              'relative max-w-none [&>pre]:p-2 [&>pre]:rounded-lg focus:outline-none min-h-[60px] text-neutral-800 scale-tailwind wysiwyg wysiwyg-chat wysiwyg-sm',
              className,
            ),
          ),
        },
      },
    },
    [enableMath, defaultTokens?.mathToken],
  );

  if (!editor) {
    return null;
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      <DefaultTokenConfigWatcher defaultTokens={defaultTokens} />
      <EditorRendererContext.Provider
        value={{ editorRef: editorRef as MutableRefObject<HTMLDivElement> }}
      >
        {children}
      </EditorRendererContext.Provider>
    </EditorContext.Provider>
  );
}

export default memo(MarkdownEditorContext);
